import type { CentroDeCusto } from '../../api/comprasDashboardApi';

/**
 * Quais blocos o painel monta, conforme os centros de custo escolhidos (at-111, reancorado no
 * centro de custo na at-112).
 *
 * 🔴 **Uma definição só, lida pela página E pelo hook.** A página decide o que renderizar e o hook
 * decide o que buscar; com duas cópias da regra, um bloco montado deixaria de ter carga (abrindo
 * eternamente "carregando") ou uma carga aconteceria para um bloco que ninguém mostra — gastando
 * requisição para nada. É a mesma razão pela qual o corte do mês virou uma função só no backend.
 *
 * O porquê de cada linha, medido em 21/09/2026 sobre as 790 requisições que não eram de peça:
 *
 * | Bloco | Só centros da oficina | Nenhum da oficina | Misto ou sem filtro |
 * |---|---|---|---|
 * | Oficina, placa, entrega no prazo | sim | **não** — oficina aparece em 2 de 790, e placa é ruído do parser | sim, com a cobertura que já declaram |
 * | Requisições por centro de custo | sim | sim | sim |
 * | Requisições por solicitante | não | sim | sim |
 * | Itens mais requisitados | não | sim | **não** — itens de peça são milhares de códigos e achatam o ranking |
 */
export interface BlocosDoModo {
  /** Oficina, placa e entrega no prazo: só onde a peça é o assunto. */
  cardsDePeca: boolean;
  /** O card que prova que a soma das linhas é o total. Existe sempre. */
  porCentroCusto: boolean;
  solicitantes: boolean;
  itens: boolean;
}

/**
 * ⚠️ **Sem seleção, o modo é "misto"** — e não "peças", como era o default do filtro de setor.
 * Com 26 centros nomeados, abrir num recorte esconderia do usuário o que ele nem sabe que existe.
 *
 * ⚠️ **Cadastro ainda não carregado cai em misto**, nunca em "outros": não saber o grupo de um
 * centro não é o mesmo que saber que ele não é da oficina, e esconder os cards de peça por
 * ignorância deixaria a tela mais pobre justamente enquanto ela carrega.
 */
export function blocosDoModo(
  centrosSelecionados: string[] | undefined,
  cadastro: CentroDeCusto[] | undefined,
): BlocosDoModo {
  const escolhidos = centrosSelecionados ?? [];
  const grupos = new Map((cadastro ?? []).map((c) => [c.pk, c.grupo]));
  const conhecidos = escolhidos.filter((pk) => grupos.has(pk));

  const daOficina = conhecidos.filter((pk) => grupos.get(pk) === 'PECAS').length;
  const soDaOficina = escolhidos.length > 0 && conhecidos.length === escolhidos.length && daOficina === escolhidos.length;
  const nenhumDaOficina = escolhidos.length > 0 && conhecidos.length === escolhidos.length && daOficina === 0;

  return {
    cardsDePeca: !nenhumDaOficina,
    porCentroCusto: true,
    solicitantes: !soDaOficina,
    itens: nenhumDaOficina,
  };
}
